import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl, createApiHeaders, apiFetch } from '../../../config/api';
import { debugError } from '../../../utils/debugLog';

/**
 * Custom hook for managing version operations
 * Handles template loading, version CRUD operations, and state management
 */
export const useVersionManager = (templateId, tenant) => {
  // State
  const [template, setTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [policySummaryByVersion, setPolicySummaryByVersion] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load template details
  const loadTemplate = useCallback(async () => {
    try {
      setError(null);
      const headers = createApiHeaders();

      // Try advanced endpoint first to support draft versions
      let response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/advanced/${templateId}?allow_draft=true`,
        {
          headers,
        }
      );

      // If advanced endpoint doesn't exist (404), fallback to basic endpoint with status=all
      if (!response.ok && response.status === 404) {
        response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/${templateId}?status=all`,
          {
            headers,
          }
        );
      }

      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.status}`);
      }

      const data = await response.json();
      setTemplate(data);
    } catch (err) {
      setError(err.message);
    }
  }, [templateId]);

  // Load template versions
  // Fetches all pages to get all versions
  const loadVersions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = createApiHeaders();
      const allVersions = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100; // Use reasonable page size

      // Fetch all pages until we have all versions
      while (hasMore) {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        // Try advanced endpoint first, fallback to basic endpoint
        let response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/advanced/${templateId}/versions?${params.toString()}`,
          {
            headers,
          }
        );

        // If advanced endpoint doesn't exist (404), fallback to basic endpoint
        if (!response.ok && response.status === 404) {
          response = await fetch(
            `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions?${params.toString()}`,
            {
              headers,
            }
          );
        }

        if (!response.ok) {
          throw new Error(`Failed to load versions: ${response.status}`);
        }

        const data = await response.json();
        // Handle both response formats: {versions: [...]} or {templates: [...]}
        const versionsList = data.versions || data.templates || [];
        allVersions.push(...versionsList);

        // Check if there are more pages
        const total = data.total || 0;
        const currentPage = data.page || page;
        const totalPages = Math.ceil(total / pageSize);
        hasMore = currentPage < totalPages && versionsList.length === pageSize;

        page++;
      }

      setVersions(allVersions);

      // Pre-load content check policy summary for each version, using MV-based analytics only.
      try {
        const summaries = {};

        // Use MV-based analytics endpoint to get policy info for all versions in one call
        const analyticsResponse = await apiFetch(
          `/api/v1/templates/analytics/templates/${templateId}`
        );

        const versionAnalytics =
          analyticsResponse &&
          analyticsResponse.template &&
          Array.isArray(analyticsResponse.template.version_analytics)
            ? analyticsResponse.template.version_analytics
            : [];

        versionAnalytics.forEach(va => {
          const versionNumber = va.version_number;
          if (!versionNumber) return;

          const policies = Array.isArray(va.policies) ? va.policies : [];
          if (policies.length === 0) {
            summaries[versionNumber] = {
              summary: 'N/A',
              isActive: false,
            };
            return;
          }

          const names = policies.map(
            p => p.policy_name || p.name || p.policy_id || 'Unnamed Policy'
          );
          const anyEnabled =
            va.policy_enabled === true ||
            policies.some(p => p.enabled !== false);

          summaries[versionNumber] = {
            summary: names.join(', '),
            isActive: anyEnabled,
          };
        });

        setPolicySummaryByVersion(summaries);
      } catch (e) {
        debugError(
          'Error loading policy summaries from analytics MV in useVersionManager:',
          e
        );
        // On failure, fall back to empty summaries
        setPolicySummaryByVersion({});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  // Keep template.summary output_format aligned with the current ACTIVE version
  // so changing a draft's format doesn't make it look like all versions changed.
  useEffect(() => {
    if (!template || !Array.isArray(versions) || versions.length === 0) {
      return;
    }

    // Prefer active version; otherwise fall back to latest (first in list)
    const activeVersion =
      versions.find(v => (v.status || '').toLowerCase() === 'active') || null;
    const latestVersion = versions[0] || null;
    const summaryVersion = activeVersion || latestVersion;

    if (
      summaryVersion &&
      summaryVersion.output_format &&
      template.output_format !== summaryVersion.output_format
    ) {
      setTemplate(prev =>
        prev
          ? {
              ...prev,
              output_format: summaryVersion.output_format,
            }
          : prev
      );
    }
  }, [template, versions]);

  // Create new version
  const createNewVersion = async versionData => {
    try {
      setActionLoading(true);
      setError(null);

      const headers = createApiHeaders();

      const payload = {
        template: versionData.template,
        description: versionData.description,
      };

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || `Failed to create version: ${response.status}`
        );
      }

      setSuccess('New version created successfully!');
      await loadVersions();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Update version status (replaces promoteVersion)
  const updateVersionStatus = async (versionNumber, statusData) => {
    try {
      setActionLoading(true);
      setError(null);

      const headers = createApiHeaders();

      // Use advanced endpoint for status updates
      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/advanced/${templateId}/versions/${versionNumber}/status`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            status: statusData.status,
            reason: statusData.reason || null,
            create_draft_copy: statusData.create_draft_copy || false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            `Failed to update version status: ${response.status}`
        );
      }

      await response.json();
      const statusLabel =
        statusData.status.charAt(0).toUpperCase() + statusData.status.slice(1);
      setSuccess(`Version ${versionNumber} status updated to ${statusLabel}!`);
      await loadVersions();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Promote version (kept for backward compatibility, uses updateVersionStatus)
  const promoteVersion = async version => {
    // Handle both version object and version string/number
    let versionNumber;

    if (typeof version === 'object' && version !== null) {
      // If it's a version object, extract version number
      versionNumber = version.version_number;
      if (!versionNumber && version.version) {
        // If version is a string, extract the number part
        const versionStr = String(version.version);
        const match = versionStr.match(/^(\d+)/);
        versionNumber = match ? parseInt(match[1], 10) : null;
      }
    } else {
      // If it's already a string or number, convert to number
      const versionStr = String(version);
      const match = versionStr.match(/^(\d+)/);
      versionNumber = match ? parseInt(match[1], 10) : parseInt(versionStr, 10);
    }

    if (!versionNumber || isNaN(versionNumber)) {
      debugError('Unable to extract version number from:', version);
      setError('Invalid version number');
      return false;
    }

    return updateVersionStatus(versionNumber, { status: 'active' });
  };

  // Rollback to version
  const rollbackToVersion = async version => {
    try {
      setActionLoading(true);
      setError(null);

      const headers = createApiHeaders();

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions/${version}/rollback`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            `Failed to rollback to version: ${response.status}`
        );
      }

      setSuccess(`Successfully rolled back to version ${version}!`);
      await loadVersions();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Load template and versions on component mount
  useEffect(() => {
    if (templateId) {
      loadTemplate();
      loadVersions();
    }
  }, [templateId, loadTemplate, loadVersions]);

  return {
    // Data
    template,
    versions,
    policySummaryByVersion,
    loading,
    error,
    success,
    actionLoading,

    // Actions
    createNewVersion,
    promoteVersion,
    updateVersionStatus, // New function for status management
    rollbackToVersion,
    loadVersions,

    // State setters
    setError,
    setSuccess,
  };
};
